/**
 * What the search did: a progress line above the rates while it runs, and
 * below them where the rates came from, what did not deliver and why, and
 * Retry only where trying again can help (see searchReport.ts for the
 * grouping and wording).
 */
import React from 'react';

import {
  describeGroup,
  isLoud,
  needsAction,
  NoticeGroup,
  pendingText,
  routeLines,
  hasReasons,
  unservedText,
  SearchReport,
  summaryText,
} from './searchReport';
import styles from './styles.module.css';

interface SearchStatusProps {
  report: SearchReport;
  quoteCount: number;
  /** Polling is running: pending rows are still shown, and Retry waits. */
  searching: boolean;
  onRetry: (vendorSearchIds: string[]) => void;
}

/** The route list is worth opening when the line cannot name them all, or a reason goes with them. */
const showRoutes = (group: NoticeGroup): boolean =>
  group.routes.length > 1 ||
  (group.routes.length === 1 && (group.laneTotal > 1 || hasReasons(group)));

const Notice: React.FC<{
  group: NoticeGroup;
  searching: boolean;
  onRetry: (ids: string[]) => void;
}> = ({ group, searching, onRetry }) => (
  <div
    className={`${isLoud(group) ? styles.errorBanner : styles.partialNote} ${styles.vendorNotice} ${styles.bannerSpaced}`}
  >
    <span>
      {describeGroup(group)}
      {showRoutes(group) && (
        <details className={styles.routeList}>
          <summary>Show routes</summary>
          <div>{routeLines(group)}</div>
          {unservedText(group) && <div>{unservedText(group)}</div>}
        </details>
      )}
    </span>
    {group.retryIds.length > 0 && (
      <button
        type='button'
        className={styles.detailToggle}
        disabled={searching}
        onClick={() => onRetry(group.retryIds)}
      >
        {group.retryIds.length > 1 ? `Retry ${group.retryIds.length}` : 'Retry'}
      </button>
    )}
  </div>
);

export const SearchProgress: React.FC<
  Pick<SearchStatusProps, 'report' | 'searching'>
> = ({ report, searching }) => {
  // Pending rows collapse into one progress line while polling runs.
  const progress = searching ? pendingText(report) : '';
  return progress ? (
    <div className={styles.searchSummary} role='status'>
      {progress}
    </div>
  ) : null;
};

export const SearchNotices: React.FC<SearchStatusProps> = ({
  report,
  quoteCount,
  searching,
  onRetry,
}) => {
  const summary = searching ? '' : summaryText(report, quoteCount);
  const settled = report.groups.filter((group) => group.kind !== 'pending');
  // With rates to show, what needs action stays visible (with its Retry) and
  // the expected skips fold away; with no rates, every reason is the answer.
  const withRates = quoteCount > 0;
  const visible = withRates ? settled.filter(needsAction) : settled;
  const folded =
    withRates && !searching ? settled.filter((g) => !needsAction(g)) : [];
  const showSummary = Boolean(summary) && withRates;
  if (
    !showSummary &&
    !report.warnings.length &&
    !visible.length &&
    !folded.length
  )
    return null;
  return (
    <div className={styles.searchNotices}>
      {showSummary && (
        <div className={styles.searchSummary} role='status'>
          {summary}
        </div>
      )}
      {report.warnings.map((warning) => (
        <div key={warning} className={styles.partialNote}>
          {warning}
        </div>
      ))}
      {visible.map((group) => (
        <Notice
          key={group.key}
          group={group}
          searching={searching}
          onRetry={onRetry}
        />
      ))}
      {folded.length > 0 && (
        <details className={styles.statusDetails}>
          <summary>
            Details: why {folded.length === 1 ? 'one search' : 'some searches'}{' '}
            did not return rates
          </summary>
          {folded.map((group) => (
            <Notice
              key={group.key}
              group={group}
              searching={searching}
              onRetry={onRetry}
            />
          ))}
        </details>
      )}
    </div>
  );
};
